import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { useReverseFinanceRefund } from "@/hooks/useInvoices";
import type { Refund } from "@/interfaces/invoices";
import { formatAmount } from "@/utils/number-format";

const todayIso = () => new Date().toISOString().slice(0, 10);
const money = (v: string, ccy = "usd") => `${ccy.toUpperCase()} ${formatAmount(Number(v))}`;

/**
 * Reverse a recorded refund — writes a negative mirror entry (and marks the original reversed).
 * Bookkeeping-only, like the refund itself. Asks for a reason and the actual reversal date.
 */
export default function ReverseRefundDialog({
  refund,
  onClose,
}: {
  refund: Refund | null;
  onClose: () => void;
}) {
  const reverse = useReverseFinanceRefund();
  const [reason, setReason] = useState("");
  const [reversalDate, setReversalDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);

  function handleClose() {
    setReason("");
    setReversalDate(todayIso());
    setError(null);
    onClose();
  }

  function submit() {
    if (!refund) return;
    setError(null);
    if (!reversalDate) return setError("Pick the reversal date.");
    if (!reason.trim()) return setError("Enter a reason.");
    reverse.mutate(
      { refundId: refund.id, payload: { reason: reason.trim(), reversalDate } },
      { onSuccess: handleClose, onError: (e) => setError((e as Error).message) },
    );
  }

  return (
    <Dialog open={refund != null} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Reverse refund</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {refund && (
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Reverses the refund of <strong>{money(refund.amount, refund.currency)}</strong> on{" "}
            {refund.invoiceNumber ?? `invoice #${refund.invoiceId}`}. This writes an offsetting
            entry for the books — it does not change the invoice status or credits.
          </Typography>
        )}
        <TextField
          label="Reversal date"
          size="small"
          type="date"
          lang={DATE_INPUT_LANG}
          value={reversalDate}
          onChange={(e) => setReversalDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Reason"
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
        />
        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={submit} disabled={reverse.isPending}>
          {reverse.isPending ? "Reversing…" : "Reverse refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
